import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTheme } from "./ThemeProvider";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { api, accountRoleLabel } from "../lib/api";
import { countAttentionItems } from "../pages/project-detail/shared";
import {
  SquaresFour, Buildings, Warning, UsersThree, FileText,
  Sun, MoonStars, List, X, Compass, SignOut, ClockCounterClockwise, ShieldCheck, ChartLineUp, SlidersHorizontal, AddressBook, BellRinging, Archive, Key, ArrowClockwise, Wallet,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ChangePasswordCard } from "./ChangePasswordCard";
import { InstallPWA } from "./InstallPWA";

const ALL_NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, key: "dashboard" },
  { to: "/leads", label: "Leads", icon: AddressBook, key: "leads" },
  { to: "/projects", label: "Projects", icon: Buildings, key: "projects" },
  { to: "/attention", label: "Attention", icon: Warning, key: "attention" },
  { to: "/notifications", label: "Notifications", icon: BellRinging, key: "notifications" },
  { to: "/policies", label: "Policies & SOP", icon: FileText, key: "policies" },
  { to: "/performance", label: "Performance", icon: ChartLineUp, key: "performance" },
  { to: "/finance", label: "Finance Overview", icon: Wallet, key: "finance" },
  { to: "/team", label: "Team", icon: UsersThree, key: "team" },
  { to: "/permissions", label: "Roles & Permissions", icon: ShieldCheck, manager: true },
  { to: "/settings", label: "Settings", icon: SlidersHorizontal, manager: true },
  { to: "/backup", label: "Backup", icon: Archive, backup: true },
  { to: "/audit", label: "Audit Log", icon: ClockCounterClockwise, key: "audit" },
];

const RefreshButton = () => {
  const [spinning, setSpinning] = React.useState(false);
  const onClick = () => {
    if (spinning) return;
    setSpinning(true);
    window.dispatchEvent(new CustomEvent("daa-manual-refresh"));
    setTimeout(() => setSpinning(false), 700);
  };
  return (
    <button
      onClick={onClick}
      data-testid="global-refresh-btn"
      title="Refresh"
      aria-label="Refresh"
      className="p-2.5 rounded-md border border-border hover:bg-accent transition-colors duration-150"
    >
      <ArrowClockwise size={18} weight="bold" className={spinning ? "animate-spin" : ""} />
    </button>
  );
};

const RealtimeIndicator = () => {
  const { connected } = useRealtime();
  return (
    <div
      data-testid="realtime-status"
      data-connected={connected ? "true" : "false"}
      title={connected
        ? "Live — collaborative updates sync instantly"
        : "Realtime connection lost, reconnecting… (still auto-refreshing every 20s)"}
      className={`flex items-center gap-1.5 h-9 px-2.5 rounded-md border text-xs font-medium transition-colors duration-150 ${
        connected
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          : "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400"
      }`}
    >
      <span className="relative flex h-2 w-2 shrink-0">
        {!connected && <span className="absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75 animate-ping" />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-500"}`} />
      </span>
      <span className="hidden sm:inline" data-testid="realtime-status-label">{connected ? "Live" : "Reconnecting…"}</span>
    </div>
  );
};

export default function Layout({ children }) {
  const { theme, toggle } = useTheme();
  const { user, isManager, logout, sidebar, perms, impersonating, impersonator, stopImpersonate } = useAuth();
  const [cpOpen, setCpOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    if (!/^\/projects\/[^/]+/.test(loc.pathname)) sessionStorage.setItem("daa_back", loc.pathname + loc.search);
  }, [loc.pathname, loc.search]);
  const [pendingCount, setPendingCount] = useState(0);
  const [myCount, setMyCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const hasApproval = (akey) => !!(perms?.super || (perms?.approvals || []).includes(akey));
  const leadCapable = !!(perms?.super || (perms?.attention_cats || []).some((c) => ["Initial Contact", "Proposal", "Presentation", "Follow-up"].includes(c)));
  const canApproveDl = (kind) => hasApproval(kind === "design" ? "design_deadline" : "quotation_deadline");
  const canApproveEot = () => hasApproval("eot");
  const canApproveLeadReq = () => hasApproval("lead_request");
  useEffect(() => {
    if (!user) { setPendingCount(0); setMyCount(0); return; }
    let ignore = false;
    // My own attention items — items I need to handle (everyone). Now includes
    // my Lead SLA alerts so the badge = Pending approvals + Project actionable + Lead actionable.
    Promise.all([
      api.get(`/projects?scope=mine`).then((r) => r.data || []).catch(() => []),
      api.get(`/attention/missing-documents`).then((r) => r.data || {}).catch(() => ({})),
      api.get(`/settings/sla`).then((r) => r.data || {}).catch(() => ({})),
      leadCapable ? api.get(`/leads?scope=mine`).then((r) => r.data || []).catch(() => []) : Promise.resolve([]),
    ]).then(([projects, missingMap, sla, leads]) => {
      const projCount = countAttentionItems(projects, { sla, missingMap, user, attnCats: perms?.attention_cats, perms });
      const leadCats = perms?.super ? null : (perms?.attention_cats || []);
      const leadCount = (leads || []).reduce((n, l) => {
        const mine = l.assigned_consultant === user.name || (l.supporting_members || []).includes(user.name);
        if (!mine) return n;
        return n + (l.alerts || []).filter((a) => a.category !== "Consultant" && a.category !== "Decision" && a.level !== "muted" && (!leadCats || leadCats.includes(a.category))).length;
      }, 0);
      if (!ignore) setMyCount(projCount + leadCount);
    });
    // Pending approvals — items awaiting my sign-off (only roles granted approval permissions).
    if (perms?.super || (perms?.approvals || []).length > 0) {
      const scope = (perms?.super || (perms?.features || []).includes("team_all_company")) ? "all" : "dept";
      Promise.all([
        canApproveDl("design") || canApproveDl("quotation") ? api.get(`/attention/pending-deadlines?scope=${scope}`).then((r) => (r.data || []).filter((d) => canApproveDl(d.kind)).length).catch(() => 0) : Promise.resolve(0),
        canApproveEot() ? api.get(`/attention/pending-eot?scope=${scope}`).then((r) => (r.data || []).length).catch(() => 0) : Promise.resolve(0),
        canApproveLeadReq() ? api.get(`/attention/pending-lead-requests`).then((r) => (r.data || []).length).catch(() => 0) : Promise.resolve(0),
      ]).then(([dl, eot, lr]) => { if (!ignore) setPendingCount(dl + eot + lr); });
    } else {
      setPendingCount(0);
    }
    return () => { ignore = true; };
  }, [user, loc.pathname, perms]);

  useEffect(() => {
    if (!user || sidebar("notifications") === "none") { setNotifCount(0); return; }
    let ignore = false;
    const fetchNotif = () => api.get("/notifications/unread-count").then((r) => { if (!ignore) setNotifCount(r.data?.unread || 0); }).catch(() => {});
    fetchNotif();
    const onTick = () => { if (!document.hidden) fetchNotif(); };
    const iv = setInterval(onTick, 20000);
    window.addEventListener("daa-notif-updated", fetchNotif);
    window.addEventListener("focus", onTick);
    window.addEventListener("daa-realtime", onTick);
    window.addEventListener("daa-manual-refresh", fetchNotif);
    return () => { ignore = true; clearInterval(iv); window.removeEventListener("daa-notif-updated", fetchNotif); window.removeEventListener("focus", onTick); window.removeEventListener("daa-realtime", onTick); window.removeEventListener("daa-manual-refresh", fetchNotif); };
  }, [user, loc.pathname, perms]); // eslint-disable-line react-hooks/exhaustive-deps

  const NAV = ALL_NAV.filter((n) => (n.backup ? ["Admin", "Managing Director"].includes(user?.role) : n.manager ? isManager : sidebar(n.key) !== "none"));
  const current = NAV.find((n) => (n.end ? loc.pathname === "/" : loc.pathname.startsWith(n.to)))?.label || "Dashboard";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 border-r border-border bg-card flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-16 flex items-center gap-3 px-6 border-b border-border">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
            <Compass weight="duotone" size={22} />
          </div>
          <div>
            <div className="font-display font-bold leading-none tracking-tight text-[14px]">DAA Project</div>
            <div className="overline text-muted-foreground mt-1">Management System</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-primary/12 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <n.icon weight="duotone" size={20} />
              {n.label}
              {n.to === "/attention" && (pendingCount + myCount) > 0 && <span className="ml-auto min-w-[20px] text-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid="nav-attention-badge" title={`${pendingCount + myCount} item${(pendingCount + myCount) > 1 ? "s" : ""} need your attention${pendingCount > 0 ? ` (${pendingCount} pending your approval)` : ""}`}>{pendingCount + myCount}</span>}
              {n.to === "/notifications" && notifCount > 0 && <span className="ml-auto min-w-[20px] text-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-500 text-white" data-testid="nav-notifications-badge" title={`${notifCount} unread update${notifCount > 1 ? "s" : ""}`}>{notifCount}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="overline text-muted-foreground mt-0.5 truncate">{accountRoleLabel(user?.role)} · {user?.department}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setCpOpen(true)}
                data-testid="open-change-password"
                title="Change password"
                className="p-2 rounded-md border border-border hover:bg-accent text-muted-foreground hover:text-primary transition-colors"
              >
                <Key size={16} />
              </button>
              <button
                onClick={logout}
                data-testid="logout-btn"
                title="Sign out"
                className="p-2 rounded-md border border-border hover:bg-accent text-muted-foreground hover:text-red-500 transition-colors"
              >
                <SignOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {impersonating && (
          <div className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 sm:px-8 py-2 bg-amber-500 text-black text-sm font-medium no-print" data-testid="impersonation-banner">
            <span className="flex items-center gap-2 min-w-0">
              <ShieldCheck size={16} weight="fill" className="shrink-0" />
              <span className="truncate">You are viewing as <strong>{user?.name}</strong>{impersonator ? ` · signed in by ${impersonator}` : ""}</span>
            </span>
            <button onClick={stopImpersonate} data-testid="exit-impersonation-btn" className="shrink-0 px-3 py-1 rounded-md bg-black/80 text-white hover:bg-black transition-colors">Exit</button>
          </div>
        )}
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 sm:px-8 border-b border-border glass no-print">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-accent transition-colors"
              onClick={() => setOpen(!open)}
              data-testid="menu-toggle"
            >
              {open ? <X size={20} /> : <List size={20} />}
            </button>
            <div>
              <h1 className="font-display font-bold text-lg tracking-tight leading-none">{current}</h1>
              <div className="overline text-muted-foreground mt-1 hidden sm:block">DAA Project Management System</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <InstallPWA />
            <RealtimeIndicator />
            <RefreshButton />
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              className="p-2.5 rounded-md border border-border hover:bg-accent transition-colors duration-150"
            >
              {theme === "dark" ? <Sun size={18} weight="duotone" /> : <MoonStars size={18} weight="duotone" />}
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-[1600px] w-full">{children}</main>
      </div>
      <Dialog open={cpOpen} onOpenChange={setCpOpen}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <ChangePasswordCard onDone={() => setCpOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
